import React, { memo, useCallback, useState } from 'react';
import { pick } from 'ramda';
import { useDeviceSelector } from 'js/store/hooks';
import { dispatcher } from 'dispatcher';

import { GameInfoMobileProps } from './types';
import { PortraitGameInfoMobile } from './components/PortraitGameInfoMobile';
import { LandscapeGameInfoMobile } from './components/LandscapeGameInfoMobile';
import style from './GameInfoMobile.scss';
import { GameInfoLoader } from './components/GameInfoLoader';
import { useShouldShowGameInfoLoader } from './hooks/useShouldShowGameInfoLoader';
import { useRequestAnimationFrame } from '@theme/js/hooks/swiss/useRequestAnimationFrame';
import { useAddAnimationOnShowHideDetails } from './hooks/useAddAnimationOnShowHideDetails';
import { ANIMATE_ON_HIDE_DETAILS, ANIMATE_ON_SHOW_DETAILS, CONTAINER } from '../context/constants';
import { useAddAnimateOnOpenClass } from './hooks/useAddAnimateOnOpenClass';
import { useAddAnimationOnOrientationChange } from './hooks/useAddAnimationOnOrientationChange';
import { useAnimationContext, useAnimationHandlersContext } from '../context/useAnimationContext';
import { clsx } from 'js/utils/classNames.util';
import { preventSwipeOnScroll } from '../utils';
import { useHideContentAndSaveScroll } from './hooks/useHideContentAndSaveScroll';

export const GameInfoMobile = ({
    image,
    name,
    funSupport,
    showFavorite,
    description,
    code,
    hideButtons,
    gameLaunchType,
    isNew,
    hot,
    popular,
    exclusive,
    descriptionGameProvider,
    longImg,
    t,
    gameDescription
}: GameInfoMobileProps) => {
    const [showGameDetails, setShowGameDetails] = useState(false);

    const {
        handleRemoveOnShowHideDetailsAnimationClasses: handleRemoveOnShowDetailsAnimationClasses,
        handleSetOnShowHideDetailsAnimationClasses: handleSetOnShowDetailsAnimationClasses
    } = useAddAnimationOnShowHideDetails({
        animationType: ANIMATE_ON_SHOW_DETAILS
    });

    const {
        handleRemoveOnShowHideDetailsAnimationClasses: handleRemoveOnHideDetailsAnimationClasses,
        handleSetOnShowHideDetailsAnimationClasses: handleSetOnHideDetailsAnimationClasses
    } = useAddAnimationOnShowHideDetails({
        animationType: ANIMATE_ON_HIDE_DETAILS
    });

    useAddAnimateOnOpenClass();
    useAddAnimationOnOrientationChange();

    const { animationContainerClasses } = useAnimationContext();

    const handleShowGameDetailsCb = useCallback(() => {
        setShowGameDetails(!showGameDetails);
        dispatcher.dispatch('gamesInfoPopupShowHideDetails');
        if (!showGameDetails) {
            handleSetOnShowDetailsAnimationClasses();
            handleRemoveOnHideDetailsAnimationClasses();
        } else {
            handleRemoveOnShowDetailsAnimationClasses();
            handleSetOnHideDetailsAnimationClasses();
        }
    }, [
        handleRemoveOnHideDetailsAnimationClasses,
        handleRemoveOnShowDetailsAnimationClasses,
        handleSetOnHideDetailsAnimationClasses,
        handleSetOnShowDetailsAnimationClasses,
        showGameDetails
    ]);

    const handleShowGameDetails = useRequestAnimationFrame(handleShowGameDetailsCb);

    const { isLandscape, os, osVersion } = useDeviceSelector(
        pick(['isLandscape', 'os', 'osVersion'])
    );

    const souldAddMarginInsteadOfGap = os === 'ios' && parseInt(osVersion) < 15;

    const showLoader = useShouldShowGameInfoLoader();
    const { handleSetPrevAnimationEnd } = useAnimationHandlersContext();

    useHideContentAndSaveScroll();

    return (
        <>
            <GameInfoLoader isVisible={showLoader} />
            {!showLoader && (
                <div
                    onTouchStart={preventSwipeOnScroll}
                    onAnimationEnd={handleSetPrevAnimationEnd(CONTAINER)}
                    className={
                    clsx([
                        style.container,
                        ...animationContainerClasses,
                        souldAddMarginInsteadOfGap ? 'oldOsVersion' : ''
                    ])}>
                {isLandscape ? (
                        <LandscapeGameInfoMobile
                            image={image}
                            name={name}
                            funSupport={funSupport}
                            showFavorite={showFavorite}
                            description={description}
                            code={code}
                            hideButtons={hideButtons}
                            gameLaunchType={gameLaunchType}
                            isNew={isNew}
                            hot={hot}
                            popular={popular}
                            exclusive={exclusive}
                            descriptionGameProvider={descriptionGameProvider}
                            longImg={longImg}
                            t={t}
                            gameDescription={gameDescription}
                            handleShowGameDetails={handleShowGameDetails}
                            showGameDetails={showGameDetails}
                        />) : (
                            <PortraitGameInfoMobile
                                image={image}
                                name={name}
                                funSupport={funSupport}
                                showFavorite={showFavorite}
                                description={description}
                                code={code}
                                hideButtons={hideButtons}
                                gameLaunchType={gameLaunchType}
                                isNew={isNew}
                                hot={hot}
                                popular={popular}
                                exclusive={exclusive}
                                descriptionGameProvider={descriptionGameProvider}
                                longImg={longImg}
                                t={t}
                                gameDescription={gameDescription}
                                handleShowGameDetails={handleShowGameDetails}
                                showGameDetails={showGameDetails}
                            />
                    )}
            </div>
            )}
        </>
    );
};

export default memo(GameInfoMobile);
